import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSparkleTrail } from '../../hooks2/useSparkleTrail';

/**
 * TaskCreationFlow - Transform Choice Cards into actionable task guidance
 * Features progressive step reveal, completion tracking, and celebration
 */
const TaskCreationFlow = ({
  choiceCard = null,
  onTaskGenerated = () => {},
  onStepComplete = () => {},
  onTaskComplete = () => {},
  onClose = () => {},
  darkMode = false,
  animationPreferences = { enabled: true },
  className = ""
}) => {
  const [flowState, setFlowState] = useState('idle'); // idle -> creating -> revealing -> active -> completed
  const [currentTask, setCurrentTask] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [revealedSteps, setRevealedSteps] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  const containerRef = useRef(null);
  const { isTrailActive, completeTrail } = useSparkleTrail();

  // Mock task data (will be replaced with agent-generated content)
  const mockTaskData = {
    task_title: choiceCard?.title || "SEO Optimization Task",
    steps: [
      "Navigate to your website's admin dashboard",
      "Go to SEO settings or meta description section", 
      "Update the meta description to be more compelling and include target keywords",
      "Save changes and clear any caches",
      "Use Google's Rich Results Test to verify the changes"
    ],
    platform_notes: "For WordPress users: You can find this in Yoast SEO or RankMath plugin settings",
    estimated_time: "10-15 minutes",
    success_criteria: "Meta description appears correctly in search results preview",
    follow_up_suggestion: "Monitor click-through rates in Google Search Console over the next week"
  };

  // Start task creation flow
  useEffect(() => {
    if (choiceCard && flowState === 'idle') {
      setFlowState('creating');
      
      // Simulate task generation (will be replaced with agent call)
      setTimeout(() => {
        setCurrentTask(mockTaskData);
        setFlowState('revealing');
        onTaskGenerated(mockTaskData);
        
        // Start progressive step reveal
        revealStepsProgressively();
      }, 2000);
    }
  }, [choiceCard, flowState]);

  // Progressive step reveal animation
  const revealStepsProgressively = () => {
    if (!mockTaskData.steps) return;
    
    let stepIndex = 0;
    const revealInterval = setInterval(() => {
      setRevealedSteps(prev => prev + 1);
      stepIndex++;
      
      if (stepIndex >= mockTaskData.steps.length) {
        clearInterval(revealInterval);
        setFlowState('active');
      }
    }, 600); // 600ms between each step reveal
  };

  // Handle step completion
  const handleStepComplete = (stepIndex) => {
    setCompletedSteps(prev => {
      const newCompleted = new Set(prev);
      if (newCompleted.has(stepIndex)) {
        newCompleted.delete(stepIndex);
      } else {
        newCompleted.add(stepIndex);
      }
      
      onStepComplete(stepIndex, newCompleted.has(stepIndex));
      
      // Check if all steps are completed
      if (newCompleted.size === currentTask?.steps?.length) {
        setTimeout(() => {
          setFlowState('completed');
          setShowCelebration(true);
          onTaskComplete();
        }, 300);
      }
      
      return newCompleted;
    });
  };

  // Handle task restart
  const handleRestart = () => {
    setCompletedSteps(new Set());
    setFlowState('active');
    setShowCelebration(false);
  };

  if (!choiceCard) return null;

  return (
    <div 
      ref={containerRef}
      className={`task-creation-flow relative ${className}`}
    >
      {/* Creating State - Loading animation */}
      {flowState === 'creating' && (
        <TaskCreationLoader darkMode={darkMode} choiceCard={choiceCard} />
      )}

      {/* Task Content - Revealing and Active states */}
      {(flowState === 'revealing' || flowState === 'active' || flowState === 'completed') && currentTask && (
        <TaskContent
          task={currentTask}
          flowState={flowState}
          revealedSteps={revealedSteps}
          completedSteps={completedSteps}
          onStepComplete={handleStepComplete}
          onRestart={handleRestart}
          onClose={onClose}
          darkMode={darkMode}
          animationPreferences={animationPreferences}
        />
      )}

      {/* Completion Celebration */}
      <TaskCompletionCelebration
        isVisible={showCelebration}
        task={currentTask}
        onRestart={handleRestart}
        onClose={onClose}
        darkMode={darkMode}
      />
    </div>
  );
};

// Task Creation Loader Component
const TaskCreationLoader = ({ darkMode, choiceCard }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    className={`
      flex flex-col items-center justify-center p-8 rounded-lg
      ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
      border shadow-lg
    `}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-500'} animate-bounce`} style={{ animationDelay: '0ms' }} />
      <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-500'} animate-bounce`} style={{ animationDelay: '150ms' }} />
      <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-500'} animate-bounce`} style={{ animationDelay: '300ms' }} />
    </div>
    
    <div className={`text-lg font-semibold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
      Creating step-by-step task...
    </div>
    
    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
      Analyzing "{choiceCard.title}" with your site context
    </div>
  </motion.div>
);

// Main Task Content Component
const TaskContent = ({
  task,
  flowState,
  revealedSteps,
  completedSteps,
  onStepComplete,
  onRestart,
  onClose,
  darkMode,
  animationPreferences
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`
      task-content rounded-lg shadow-lg overflow-hidden
      ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
      border
    `}
  >
    {/* Task Header */}
    <div className={`
      p-6 border-b
      ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}
    `}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {task.task_title}
          </h3>
          
          <div className="flex items-center gap-4 text-sm">
            <div className={`flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {task.estimated_time}
            </div>
            
            <div className={`flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {completedSteps.size} of {task.steps.length} steps completed
            </div>
          </div>
        </div>
        
        <button
          onClick={onClose}
          className={`
            p-2 rounded-lg transition-colors
            ${darkMode 
              ? 'hover:bg-gray-700 text-gray-400' 
              : 'hover:bg-gray-200 text-gray-600'
            }
          `}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    {/* Task Steps */}
    <div className="p-6">
      <div className="space-y-4">
        {task.steps.map((step, index) => (
          <TaskStep
            key={index}
            step={step}
            index={index}
            isRevealed={index < revealedSteps}
            isCompleted={completedSteps.has(index)}
            onToggle={() => onStepComplete(index)}
            darkMode={darkMode}
            animationPreferences={animationPreferences}
          />
        ))}
      </div>

      {/* Platform Notes */}
      {task.platform_notes && flowState === 'active' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`
            mt-6 p-4 rounded-lg border-l-4 border-blue-500
            ${darkMode ? 'bg-blue-900/20 text-blue-200' : 'bg-blue-50 text-blue-800'}
          `}
        >
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium mb-1">Platform-specific tip:</div>
              <div className="text-sm">{task.platform_notes}</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Success Criteria */}
      {task.success_criteria && flowState === 'active' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className={`
            mt-4 p-4 rounded-lg border-l-4 border-green-500
            ${darkMode ? 'bg-green-900/20 text-green-200' : 'bg-green-50 text-green-800'}
          `}
        >
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium mb-1">Success criteria:</div>
              <div className="text-sm">{task.success_criteria}</div>
            </div>
          </div>
        </motion.div>
      )}
    </div>

    {/* Task Actions */}
    {flowState === 'completed' && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`
          p-6 border-t flex items-center justify-between
          ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}
        `}
      >
        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          All steps completed! 🎉
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={onRestart}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }
            `}
          >
            Reset Task
          </button>
          
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Done
          </button>
        </div>
      </motion.div>
    )}
  </motion.div>
);

// Individual Task Step Component
const TaskStep = ({ 
  step, 
  index, 
  isRevealed, 
  isCompleted, 
  onToggle, 
  darkMode, 
  animationPreferences 
}) => {
  if (!isRevealed) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ 
        delay: index * 0.1,
        type: "spring", 
        damping: 25, 
        stiffness: 300 
      }}
      className={`
        task-step flex items-start gap-3 p-4 rounded-lg transition-all duration-300
        ${isCompleted 
          ? darkMode 
            ? 'bg-green-900/20 border-green-700' 
            : 'bg-green-50 border-green-200'
          : darkMode 
            ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700' 
            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
        }
        border cursor-pointer
      `}
      onClick={onToggle}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0 mt-0.5">
        <div className={`
          w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200
          ${isCompleted
            ? 'bg-green-500 border-green-500'
            : darkMode
              ? 'border-gray-500 hover:border-gray-400'
              : 'border-gray-300 hover:border-gray-400'
          }
        `}>
          {isCompleted && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1">
        <div className={`
          text-sm leading-relaxed transition-all duration-200
          ${isCompleted 
            ? darkMode 
              ? 'text-green-200 line-through opacity-75' 
              : 'text-green-800 line-through opacity-75'
            : darkMode 
              ? 'text-gray-200' 
              : 'text-gray-800'
          }
        `}>
          {step}
        </div>
      </div>
    </motion.div>
  );
};

// Task Completion Celebration Component
const TaskCompletionCelebration = ({ 
  isVisible, 
  task, 
  onRestart, 
  onClose, 
  darkMode 
}) => {
  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="fixed inset-0 flex items-center justify-center z-[10000] p-4"
      style={{ 
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)'
      }}
    >
      <motion.div
        initial={{ y: 50 }}
        animate={{ y: 0 }}
        className={`
          max-w-md w-full p-8 rounded-xl text-center
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border shadow-2xl
        `}
      >
        <div className="text-6xl mb-4">🎉</div>
        
        <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Task Completed!
        </h3>
        
        <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Great job! You've successfully completed "{task?.task_title}".
        </p>

        {task?.follow_up_suggestion && (
          <div className={`
            p-4 rounded-lg mb-6 text-left
            ${darkMode ? 'bg-blue-900/20 text-blue-200' : 'bg-blue-50 text-blue-800'}
          `}>
            <div className="font-medium mb-1">Next step:</div>
            <div className="text-sm">{task.follow_up_suggestion}</div>
          </div>
        )}
        
        <div className="flex items-center gap-3 justify-center">
          <button
            onClick={onRestart}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }
            `}
          >
            Reset Task
          </button>
          
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Awesome!
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TaskCreationFlow;
